import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Loader2, Users, Image as ImageIcon, Film, Mic, Layers,
  Briefcase, Euro, Mail, TrendingUp, ShieldAlert, RefreshCw,
} from "lucide-react";

const KIND_META = {
  flyer: { label: "Flyers", icon: ImageIcon, color: "#FBBF24" },
  video: { label: "Vidéos", icon: Film,      color: "#A78BFA" },
  voice: { label: "Voix off", icon: Mic,     color: "#60A5FA" },
  ad:    { label: "Pubs finales", icon: Layers, color: "#34D399" },
};

const STATUS_LABEL = {
  pending: "En attente",
  accepted: "Acceptés",
  in_progress: "En cours",
  delivered: "Livrés",
  paid: "Payés",
  cancelled: "Annulés",
};

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="studio-card p-5">
      <div className="flex items-center justify-between">
        <div className="label-mono">{label}</div>
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
            accent ? "bg-gold text-black" : "bg-white/5 text-gold border border-gold/30"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-heading tracking-tight" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-white/40">{sub}</div>}
    </div>
  );
}

function ProgressRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-white/80">{label}</span>
        <span className="text-white/50 text-xs">{count} · {pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

export default function AdminStats() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setError("Non authentifié");
      setLoading(false);
      return;
    }
    fetchStats();
  }, [authLoading, user]);

  if (loading || authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <ShieldAlert className="w-14 h-14 text-red-400 mx-auto" />
        <h1 className="font-heading text-3xl mt-4">Accès refusé</h1>
        <p className="text-white/60 mt-2">{error}</p>
        <Link to="/dashboard" className="ghost-btn mt-6 inline-flex">Retour au dashboard</Link>
      </div>
    );
  }

  const { users, projects, briefs, revenue, newsletter, recent_projects } = stats;
  const total = projects.total || 1;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-mono">ADMINISTRATION · STATISTIQUES GLOBALES</div>
          <h1 className="font-heading text-4xl tracking-tighter mt-2">
            Vue d&apos;ensemble <span className="gold-text">XookPub</span>
          </h1>
          <p className="text-white/50 text-sm mt-2">
            Dernière mise à jour : {new Date(stats.generated_at).toLocaleString("fr-FR")}
          </p>
        </div>
        <button
          onClick={() => { setRefreshing(true); fetchStats(); }}
          disabled={refreshing}
          data-testid="admin-refresh"
          className="ghost-btn !py-2 !px-4 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Rafraîchir
        </button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi
          icon={Euro}
          label="Revenu total"
          value={`${revenue.grand_total_eur.toLocaleString("fr-FR")} €`}
          sub={`Stripe ${revenue.stripe_total_eur}€ · Briefs ${revenue.agency_briefs_total_eur}€`}
          accent
        />
        <Kpi
          icon={Users}
          label="Utilisateurs"
          value={users.total}
          sub={`+${users.new_7d} sur 7 jours · ${users.email} email · ${users.google} Google`}
        />
        <Kpi
          icon={TrendingUp}
          label="Créations IA"
          value={projects.total}
          sub={`+${projects.last_7d} sur 7 jours`}
        />
        <Kpi
          icon={Briefcase}
          label="Briefs agence"
          value={briefs.total}
          sub={`${briefs.paid} payés`}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Generation breakdown */}
        <div className="studio-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl">Répartition par type</h2>
            <span className="text-white/40 text-xs">Total {projects.total}</span>
          </div>
          <div className="space-y-4">
            {Object.entries(KIND_META).map(([key, meta]) => (
              <ProgressRow
                key={key}
                label={meta.label}
                count={projects.by_kind[key] || 0}
                total={total}
                color={meta.color}
              />
            ))}
          </div>
        </div>

        {/* Brief funnel */}
        <div className="studio-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl">Pipeline agence</h2>
            <span className="text-white/40 text-xs">{briefs.total} briefs</span>
          </div>
          {Object.keys(briefs.by_status).length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Aucun brief reçu pour le moment</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(briefs.by_status).map(([status, count]) => (
                <ProgressRow
                  key={status}
                  label={STATUS_LABEL[status] || status}
                  count={count}
                  total={briefs.total || 1}
                  color={status === "paid" ? "#34D399" : status === "delivered" ? "#FBBF24" : "#9CA3AF"}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Newsletter + Recent activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="studio-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-white/5 border border-gold/30 flex items-center justify-center text-gold">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="label-mono">Newsletter</div>
              <div className="font-heading text-2xl mt-1" data-testid="kpi-newsletter-total">{newsletter.total}</div>
            </div>
          </div>
          <p className="text-xs text-white/50">
            +{newsletter.new_7d} nouveaux sur les 7 derniers jours
          </p>
        </div>

        <div className="studio-card p-6 lg:col-span-2">
          <h2 className="font-heading text-xl mb-4">Activité récente</h2>
          {recent_projects.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Aucune création récente</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto scroll-thin">
              {recent_projects.map((p, i) => {
                const meta = KIND_META[p.kind] || { label: p.kind, icon: Layers, color: "#9CA3AF" };
                const PIcon = meta.icon;
                return (
                  <div
                    key={p.project_id || `${p.created_at}-${p.user_id}-${i}`}
                    className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/5 transition"
                    data-testid={`activity-row-${i}`}
                  >
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: `${meta.color}20`, color: meta.color }}
                    >
                      <PIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate" title={p.title}>{p.title || "Sans titre"}</div>
                      <div className="text-[11px] text-white/40">
                        {meta.label}{p.subkind ? ` · ${p.subkind}` : ""} · {p.user_id?.slice(-6)}
                      </div>
                    </div>
                    <div className="text-[11px] text-white/40 shrink-0">
                      {new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
